import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { accessShare } from '../services/api'

export function SharedFile() {
  const { shareId } = useParams<{ shareId: string }>()
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloadInfo, setDownloadInfo] = useState<{
    downloadUrl: string
    filename: string
  } | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!shareId || !password.trim()) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await accessShare(shareId, password)
      setDownloadInfo(result)
    } catch (err: unknown) {
      // Extract error message from axios error response
      let message = 'Failed to access file. Please try again.'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } }
        const apiMessage = axiosError.response?.data?.message?.toLowerCase() || ''
        if (apiMessage.includes('not found') || apiMessage.includes('expired')) {
          message = 'This share link has expired or does not exist.'
        } else if (apiMessage.includes('invalid password')) {
          message = 'Invalid password. Please try again.'
        }
      } else if (err instanceof Error) {
        const errMessage = err.message.toLowerCase()
        if (errMessage.includes('not found') || errMessage.includes('expired')) {
          message = 'This share link has expired or does not exist.'
        } else if (errMessage.includes('invalid password')) {
          message = 'Invalid password. Please try again.'
        }
      }
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = () => {
    if (downloadInfo) {
      const link = document.createElement('a')
      link.href = downloadInfo.downloadUrl
      link.download = downloadInfo.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  if (!shareId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-4">Invalid Link</h1>
          <p className="text-gray-600">This share link is invalid.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <div className="text-center mb-6">
          <svg
            className="mx-auto h-12 w-12 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            Shared File
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Enter the password to access this file
          </p>
        </div>

        {!downloadInfo ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Verifying...' : 'Access File'}
            </button>
          </form>
        ) : (
          <div className="text-center">
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <svg
                className="mx-auto h-8 w-8 text-green-600 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <p className="text-sm font-medium text-green-800">
                Password verified!
              </p>
              <p className="text-xs text-green-600 mt-1">
                {downloadInfo.filename}
              </p>
            </div>

            <button
              onClick={handleDownload}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Download File
            </button>

            <p className="mt-3 text-xs text-gray-500">
              This download link will expire soon
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
